import type {
  AdminClient,
  PerGameReport,
  RecentBetsReport,
  RecentBigWinsReport,
  TopBettorsReport,
} from '@apx/admin-sdk';
import { applySort, getSortState, sortableTh, wireSortHeaders } from '../sort.js';
import { renderPager, bindPager } from '../pager.js';
import { emptyState } from '../empty-state.js';
import { skeletonTable } from '../skeleton.js';
import { fmtDate, fmtDateTime, fmtMoney, fmtTime } from '../format.js';
import { toMajorNumber } from '@apx/money';
import { acceptStatusCell, escapeHtml, money, pct, txIdCell } from './reports-shared.js';

export interface TableCtx {
  /** Currency the report is scoped to (native model — every money metric is
   *  per-currency). Empty string when the integrator has no enabled currency. */
  currency: string;
  integratorId: string;
  /** When provided, each round row gets an eye-icon button that opens the hosted report. */
  client?: AdminClient;
  /** IANA tz name for rendering per-row timestamps — defaults to browser-local when omitted. */
  tz?: string;
}

export interface AutoPlaySession {
  sessionId: string;
  playerId: string;
  displayName: string;
  target: string;
  rounds: number;
  /** The player's currency; the three amounts are integer minor units of it. */
  currency: string;
  totalStaked: string;
  totalPayout: string;
  net: string;
  firstAt: string;
  lastAt: string;
}

export interface AutoPlayPage {
  total: number;
  totalCapped: boolean;
  limit: number;
  sessions: AutoPlaySession[];
}

function href(integratorId: string, sub: string): string {
  return `/integrators/${encodeURIComponent(integratorId)}/${sub}`;
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Eye-icon button HTML for a round row — triggers the report URL flow.
 *  Pass betId on per-bet rows so the report opens bet-scoped. */
function eyeBtn(roundId: string, betId?: string): string {
  return `<button
    data-view-report="${escapeHtml(roundId)}"${betId ? ` data-view-report-bet="${escapeHtml(betId)}"` : ''}
    title="Open ${betId ? 'bet' : 'round'} report"
    style="background:none;border:none;padding:4px 6px;border-radius:4px;cursor:pointer;color:var(--text-dim);display:inline-flex;align-items:center;vertical-align:middle"
  ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg></button>`;
}

/** Wire delegated click handler on `el` for [data-view-report] buttons.
 *  Stops propagation so the row-nav href doesn't fire alongside it. */
function wireReportBtns(el: HTMLElement, ctx: TableCtx): void {
  if (!ctx.client) return;
  el.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-view-report]');
    if (!btn) return;
    e.stopPropagation();
    e.preventDefault();
    const roundId = btn.dataset.viewReport!;
    const betId = btn.dataset.viewReportBet;
    btn.setAttribute('disabled', '');
    ctx.client!.getRoundReportUrl(roundId, betId).then(({ reportUrl }) => {
      window.open(reportUrl, '_blank', 'noopener');
    }).catch((err: unknown) => {
      // Surface error briefly on the button's title so it's visible without needing a toast import.
      btn.setAttribute('title', `Error: ${describe(err)}`);
      setTimeout(() => btn.setAttribute('title', 'Open round report'), 4000);
    }).finally(() => {
      btn.removeAttribute('disabled');
    });
  });
}

/** Per-game (product) breakdown — fully loaded, client-sorted, with a totals footer. */
export function mountPerGame(
  el: HTMLElement,
  games: PerGameReport['games'],
  ctx: TableCtx,
): void {
  const TABLE = 'reports-per-game';
  function render(): void {
    if (games.length === 0) {
      el.innerHTML = emptyState({ icon: 'list', title: 'No activity in this window' });
      return;
    }
    const ccyLabel = ctx.currency || undefined;
    // Server-scoped to one currency, so the credit-native per-game totals ARE
    // that currency's amounts — label them, no fiat/blend branch.
    const view = games.map((g) => ({
      g,
      ccy: ccyLabel,
      bet: g.totalBet,
      payout: g.totalPayout,
      house: g.houseTake,
    }));
    const sort = getSortState(TABLE);
    const sorted = applySort(view, sort, (r, k) => {
      switch (k) {
        case 'game': return r.g.gameId;
        case 'bets': return r.g.betCount;
        case 'bet': return toMajorNumber(r.bet, r.ccy);
        case 'payout': return toMajorNumber(r.payout, r.ccy);
        case 'house': return toMajorNumber(r.house, r.ccy);
        case 'rtp': return r.g.rtp;
        default: return null;
      }
    });
    // BigInt accumulation — a plain `Number()` running sum loses precision on
    // large per-game totals before they ever reach the footer.
    const tot = view.reduce(
      (a, r) => ({
        bets: a.bets + r.g.betCount,
        bet: a.bet + BigInt(r.bet),
        payout: a.payout + BigInt(r.payout),
        house: a.house + BigInt(r.house),
      }),
      { bets: 0, bet: 0n, payout: 0n, house: 0n },
    );
    const totRtp = tot.bet > 0n ? Number(tot.payout) / Number(tot.bet) : 0;
    const ccy = ccyLabel;
    const amtHdr = ccyLabel ? `Total bet (${ccyLabel})` : 'Total bet';
    const payHdr = ccyLabel ? `Total payout (${ccyLabel})` : 'Total payout';
    el.innerHTML = `<div class="data-table-wrap"><table class="data-table">
      <thead><tr>
        ${sortableTh('Game', 'game', sort)}
        ${sortableTh('Bet count', 'bets', sort, { numeric: true })}
        ${sortableTh(amtHdr, 'bet', sort, { numeric: true })}
        ${sortableTh(payHdr, 'payout', sort, { numeric: true })}
        ${sortableTh('House take', 'house', sort, { numeric: true })}
        ${sortableTh('RTP', 'rtp', sort, { numeric: true })}
      </tr></thead>
      <tbody>${sorted
        .map((r) => {
          return `<tr class="row-nav" data-row-href="${href(ctx.integratorId, `reports/pl/${encodeURIComponent(r.g.gameId)}`)}" title="Open game breakdown">
            <td><code>${escapeHtml(r.g.gameId)}</code></td>
            <td class="num">${r.g.betCount.toLocaleString()}</td>
            <td class="num">${money(r.bet, r.ccy)}</td>
            <td class="num">${money(r.payout, r.ccy)}</td>
            <td class="num ${Number(r.house) >= 0 ? 'pos' : 'neg'}">${money(r.house, r.ccy)}</td>
            <td class="num">${pct(r.g.rtp)}</td>
          </tr>`;
        })
        .join('')}</tbody>
      <tfoot><tr class="data-total">
        <td>${view.length} game${view.length === 1 ? '' : 's'}</td>
        <td class="num">${tot.bets.toLocaleString()}</td>
        <td class="num">${money(tot.bet.toString(), ccy)}</td>
        <td class="num">${money(tot.payout.toString(), ccy)}</td>
        <td class="num ${tot.house >= 0n ? 'pos' : 'neg'}">${money(tot.house.toString(), ccy)}</td>
        <td class="num">${pct(totRtp)}</td>
      </tr></tfoot>
    </table></div>`;
    wireSortHeaders(el, TABLE, render);
  }
  render();
}

/**
 * Server-paged table scaffold — handles loading, error, empty, body render, and
 * the numbered pager. `fetch(page)` returns one server page (rows + total).
 */
function mountPaged<T>(
  el: HTMLElement,
  cols: number,
  label: string,
  fetchPage: (page: number, limit: number) => Promise<{ rows: T[]; total: number; limit: number; capped: boolean }>,
  renderBody: (rows: T[]) => string,
): void {
  let page = 1;
  let pageSize = 10;
  async function render(): Promise<void> {
    el.innerHTML = skeletonTable({ rows: 6, columns: cols });
    let res: { rows: T[]; total: number; limit: number; capped: boolean };
    try {
      res = await fetchPage(page, pageSize);
    } catch (err) {
      el.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
      return;
    }
    if (res.total === 0) {
      el.innerHTML = emptyState({ icon: 'list', title: `No ${label} in this window` });
      return;
    }
    el.innerHTML = `<div class="data-table-wrap"><table class="data-table">${renderBody(res.rows)}</table></div>${renderPager(page, res.total, pageSize, label, res.capped)}`;
    bindPager(
      el,
      (p) => {
        page = p;
        void render();
      },
      (size) => {
        pageSize = size;
        page = 1; // resizing invalidates the current page offset
        void render();
      },
    );
  }
  void render();
}

/** Top bettors — server-paged (canonical order: turnover desc). */
export function mountTopBettors(
  el: HTMLElement,
  ctx: TableCtx,
  fetchPage: (page: number, limit: number) => Promise<TopBettorsReport>,
): void {
  const turnHdr = ctx.currency ? `Turnover (${ctx.currency})` : 'Turnover';
  mountPaged(
    el,
    6,
    'bettors',
    async (page, limit) => {
      const r = await fetchPage(page, limit);
      return { rows: r.entries, total: r.total, limit: r.limit, capped: r.totalCapped };
    },
    (entries) => `
      <thead><tr><th class="rank">#</th><th>Player</th><th>Currency</th><th class="num">Bets</th><th class="num">${turnHdr}</th><th class="num">Net</th></tr></thead>
      <tbody>${entries
        .map((e) => {
          // Server-scoped to one currency — use the player's native turnover/net
          // and label with the row's own currency.
          const netN = Number(e.net);
          return `<tr class="row-nav" data-row-href="${href(ctx.integratorId, `players/${encodeURIComponent(e.playerId)}`)}">
            <td class="rank">${e.rank}</td>
            <td>${escapeHtml(e.displayName)}</td>
            <td><code>${escapeHtml(e.currency)}</code></td>
            <td class="num">${e.betCount.toLocaleString()}</td>
            <td class="num">${money(e.totalBet, e.currency)}</td>
            <td class="num ${netN >= 0 ? 'pos' : 'neg'}">${netN >= 0 ? '+' : ''}${money(e.net, e.currency)}</td>
          </tr>`;
        })
        .join('')}</tbody>`,
  );
}

/** Recent big wins — server-paged (canonical order: payout desc). */
export function mountRecentWins(
  el: HTMLElement,
  ctx: TableCtx,
  fetchPage: (page: number, limit: number) => Promise<RecentBigWinsReport>,
): void {
  mountPaged(
    el,
    10,
    'wins',
    async (page, limit) => {
      const r = await fetchPage(page, limit);
      return { rows: r.wins, total: r.total, limit: r.limit, capped: false };
    },
    (wins) => `
      <thead><tr><th>Transaction ID</th><th>Settled</th><th>Placed</th><th>Player</th><th>Game</th><th>Pick</th><th>Currency</th><th class="num">Bet</th><th class="num">Payout</th><th>Round</th>${ctx.client ? '<th style="width:40px"></th>' : ''}</tr></thead>
      <tbody>${wins
        .map(
          (w) => `<tr class="row-nav" data-row-href="${href(ctx.integratorId, `transaction-trace?id=${encodeURIComponent(w.roundId)}`)}" title="Open round trace">
            ${txIdCell(w.transactionId)}
            <td>${w.settledAt ? fmtDateTime(w.settledAt, ctx.tz) : '—'}</td>
            <td>${fmtDateTime(w.placedAt, ctx.tz)}</td>
            <td><a href="${href(ctx.integratorId, `players/${encodeURIComponent(w.playerId)}`)}" class="row-link">${escapeHtml(w.displayName)}</a></td>
            <td><code>${escapeHtml(w.gameId)}</code></td>
            <td><code>${escapeHtml(w.betType)}</code></td>
            <td><code>${escapeHtml(w.currency)}</code></td>
            <td class="num">${fmtMoney(w.creditAmount, w.currency)}</td>
            <td class="num pos" style="font-weight:600">${fmtMoney(w.creditPayout, w.currency)}</td>
            <td><code>${escapeHtml(w.roundId.slice(-8))}</code></td>
            ${ctx.client ? `<td style="padding:2px 4px;text-align:right;white-space:nowrap">${eyeBtn(w.roundId, w.betId)}</td>` : ''}
          </tr>`,
        )
        .join('')}</tbody>`,
  );
  wireReportBtns(el, ctx);
}

/** Recent plays — flat chronological bet feed, all outcomes, money-first.
 *  `acceptStatus` sits beside `outcome` because neither identifies a row alone:
 *  VOID + cancelled is a player UNDO, VOID + confirmed an admin refund. */
export function mountRecentBets(
  el: HTMLElement,
  ctx: TableCtx,
  fetchPage: (page: number, limit: number) => Promise<RecentBetsReport>,
): void {
  mountPaged(
    el,
    12,
    'plays',
    async (page, limit) => {
      const r = await fetchPage(page, limit);
      return { rows: r.bets, total: r.total, limit: r.limit, capped: false };
    },
    (bets) => `
      <thead><tr><th>Transaction ID</th><th>Placed</th><th>Settled</th><th>Game</th><th>Player</th><th>Pick</th><th>Currency</th><th class="num">Stake</th><th class="num">Payout</th><th class="num">Net</th><th>Outcome</th><th>Accept status</th>${ctx.client ? '<th style="width:40px"></th>' : ''}</tr></thead>
      <tbody>${bets
        .map((b) => {
          const outcomeClass = b.outcome === 'WIN' ? 'live' : b.outcome === 'VOID' ? 'warn' : '';
          // Sign only — the displayed magnitude below goes through `fmtMoney`
          // (BigInt-exact) rather than this float conversion.
          const netPositive = Number(b.net) >= 0;
          return `<tr class="row-nav" data-row-href="${href(ctx.integratorId, `transaction-trace?id=${encodeURIComponent(b.roundId)}`)}" title="Open round trace">
            ${txIdCell(b.transactionId)}
            <td>${fmtDateTime(b.placedAt, ctx.tz)}</td>
            <td>${b.settledAt ? fmtDateTime(b.settledAt, ctx.tz) : '<span class="muted">unsettled</span>'}</td>
            <td><code>${escapeHtml(b.gameId)}</code></td>
            <td><a href="${href(ctx.integratorId, `players/${encodeURIComponent(b.playerId)}`)}" class="row-link">${escapeHtml(b.displayName)}</a></td>
            <td><code>${escapeHtml(b.betType)}</code></td>
            <td><code>${escapeHtml(b.currency)}</code></td>
            <td class="num">${fmtMoney(b.creditAmount, b.currency)}</td>
            <td class="num">${fmtMoney(b.creditPayout, b.currency)}</td>
            <td class="num ${netPositive ? 'pos' : 'neg'}">${netPositive ? '+' : ''}${fmtMoney(b.net, b.currency)}</td>
            <td><span class="pill ${outcomeClass}">${escapeHtml(b.outcome)}</span></td>
            ${acceptStatusCell(b.acceptStatus)}
            ${ctx.client ? `<td style="padding:2px 4px;text-align:right;white-space:nowrap">${eyeBtn(b.roundId, b.betId)}</td>` : ''}
          </tr>`;
        })
        .join('')}</tbody>`,
  );
  wireReportBtns(el, ctx);
}

/** Auto-play sessions — server-paged (canonical order: last bet desc). */
export function mountAutoplay(
  el: HTMLElement,
  ctx: TableCtx,
  fetchPage: (page: number, limit: number) => Promise<AutoPlayPage>,
): void {
  mountPaged(
    el,
    9,
    'sessions',
    async (page, limit) => {
      const r = await fetchPage(page, limit);
      return { rows: r.sessions, total: r.total, limit: r.limit, capped: r.totalCapped };
    },
    (sessions) => `
      <thead><tr><th>Last bet</th><th>Player</th><th>Target</th><th class="num">Rounds</th><th>Currency</th><th class="num">Staked</th><th class="num">Payout</th><th class="num">Net</th><th>Session</th></tr></thead>
      <tbody>${sessions
        .map((s) => {
          // Sign only — the displayed magnitude goes through `fmtMoney`
          // (BigInt-exact) rather than this float conversion. Amounts are in
          // the player's currency, the one every bet of the session is in.
          const netSign = Number(s.net) > 0 ? 1 : Number(s.net) < 0 ? -1 : 0;
          const netStr = fmtMoney(s.net, s.currency, { signed: true });
          return `<tr class="row-nav" data-row-href="${href(ctx.integratorId, `players/${encodeURIComponent(s.playerId)}`)}">
            <td>${fmtDateTime(s.lastAt, ctx.tz)}</td>
            <td>${escapeHtml(s.displayName)}</td>
            <td><code>${escapeHtml(s.target)}</code></td>
            <td class="num">${s.rounds}</td>
            <td><code>${escapeHtml(s.currency)}</code></td>
            <td class="num">${fmtMoney(s.totalStaked, s.currency)}</td>
            <td class="num">${fmtMoney(s.totalPayout, s.currency)}</td>
            <td class="num ${netSign > 0 ? 'pos' : netSign < 0 ? 'neg' : ''}" style="font-weight:600">${netStr}</td>
            <td><code>${escapeHtml(s.sessionId.slice(-10))}</code></td>
          </tr>`;
        })
        .join('')}</tbody>`,
  );
}

export { fmtDate, fmtTime };
